/**
 * Notification Fallback (No Service Worker)
 * Uses browser Notification API directly for environments that don't support service workers
 */

/**
 * Check if notifications are supported
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window;
}

/**
 * Get current notification permission
 */
export function getNotificationPermission(): NotificationPermission {
  if (!isNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isNotificationSupported()) {
    console.warn('⚠️ Notifications not supported in this browser');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('📬 Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('❌ Error requesting notification permission:', error);
    return 'denied';
  }
}

/**
 * Show a notification (without service worker)
 */
export function showNotification(title: string, options?: NotificationOptions): Notification | null {
  const desktopNotify = (window as any).aqlDesktop?.showNotification;
  if (typeof desktopNotify === 'function') {
    desktopNotify(title, {
      body: options?.body,
      silent: options?.silent
    }).catch((error: unknown) => console.error('❌ Error showing desktop notification:', error));
    return null;
  }

  if (!isNotificationSupported()) {
    console.warn('⚠️ Notifications not supported');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('⚠️ Notification permission not granted');
    return null;
  }

  const createWindowNotification = () => {
    const notification = new Notification(title, {
      icon: '/aql-engineering-logo.png',
      badge: '/aql-engineering-logo.png',
      ...options
    });

    setTimeout(() => notification.close(), 5000);
    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return notification;
  };

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (!registration) {
        createWindowNotification();
        return;
      }
      registration.showNotification(title, {
        icon: '/aql-engineering-logo.png',
        badge: '/aql-engineering-logo.png',
        ...options
      }).catch((error) => console.error('❌ Error showing service worker notification:', error));
    }).catch(() => createWindowNotification());
    return null;
  }

  try {
    return createWindowNotification();
  } catch (error) {
    console.error('❌ Error showing notification:', error);
    return null;
  }
}

/**
 * Check if notifications are enabled for an app
 */
export function isNotificationEnabled(appName: string): boolean {
  const enabled = localStorage.getItem(`notifications-enabled-${appName}`);
  return enabled === 'true' && Notification.permission === 'granted';
}

/**
 * Enable notifications for an app
 */
export async function enableNotifications(appName: string): Promise<boolean> {
  const permission = await requestNotificationPermission();
  
  if (permission === 'granted') {
    localStorage.setItem(`notifications-enabled-${appName}`, 'true');
    console.log(`✅ Notifications enabled for ${appName}`);
    return true;
  }
  
  return false;
}

/**
 * Disable notifications for an app
 */
export function disableNotifications(appName: string): void {
  localStorage.setItem(`notifications-enabled-${appName}`, 'false');
  console.log(`🔕 Notifications disabled for ${appName}`);
}

/**
 * Send a test notification
 */
export async function sendTestNotification(): Promise<void> {
  if (Notification.permission !== 'granted') {
    throw new Error('Notification permission not granted');
  }

  showNotification('🔔 Notificação de Teste', {
    body: 'As suas notificações estão a funcionar corretamente!',
    icon: '/favicon.ico',
    tag: 'test-notification'
  });
}

/**
 * Notify task completion
 */
export function notifyTaskCompletion(taskName: string): void {
  if (!isNotificationEnabled('aql-time')) {
    return;
  }

  showNotification('Tarefa Concluída! 🎉', {
    body: `"${taskName}" foi marcada como concluída`,
    tag: 'task-completed',
    requireInteraction: false
  });
}

/**
 * Notify shared task completion
 */
export function notifySharedTaskCompletion(taskName: string, completedBy: string): void {
  if (!isNotificationEnabled('aql-time')) {
    return;
  }

  showNotification('Tarefa Partilhada Concluída', {
    body: `${completedBy} concluiu: "${taskName}"`,
    tag: 'shared-task-completed',
    requireInteraction: false
  });
}

/**
 * Notify new alert
 */
export function notifyAlert(alertTitle: string, alertMessage: string): void {
  if (!isNotificationEnabled('aql-vision')) {
    return;
  }

  showNotification('⚠️ Novo Alerta', {
    body: `${alertTitle}: ${alertMessage}`,
    tag: 'new-alert',
    requireInteraction: true
  });
}

/**
 * Initialize notification system (called on app startup)
 */
export async function initializeNotifications(): Promise<void> {
  console.log('🔔 Initializing notification system (fallback mode - no service worker)');
  
  if (!isNotificationSupported()) {
    console.warn('⚠️ Notifications not supported in this browser');
    return;
  }

  // Check if we already have permission
  if (Notification.permission === 'granted') {
    console.log('✅ Notification permission already granted');
  } else if (Notification.permission === 'default') {
    console.log('ℹ️ Notification permission not yet requested');
  } else {
    console.warn('⚠️ Notification permission denied');
  }
}
